// Local TW4 modal renderer for v7 portal-mount modals (mode 1 / mode
// 3 fallback of openPortalModal). Counterpart to the embed script's
// renderModal iframe path, framed with v7's native-<dialog> +
// widget-modal stylesheet so the donate widget's React content
// portals into a styled box that matches the surrounding v7 chrome.
//
// Per-id cache: a modal opened once stays in the DOM so a reopen
// reuses the same element (matching renderModal's modalCache shape).
// React state inside the picker / composer is owned by the React
// component — not reset here — so draft input survives close →
// reopen unless the component chooses to reset on value-prop change.
//
// Sizing opts (maxWidth, scrollWithin, clipContent) mirror what
// renderModal applies on the iframe path so the modal looks the same
// across modes. maxWidth flows through v7's --widget-modal-max-width
// custom property; scrollWithin doesn't have a counterpart in v7
// today because .widget-modal already has the right shape (dialog
// itself is the scrolling container, so content-driven height +
// dialog-scrolls-when-tall is the natural behaviour). clipContent is
// ignored here too — v7's .widget-modal-body already carries
// overflow:hidden, so it clips to the rounded corners without help
// (it's a v6-only concern, where BS5 modal-content doesn't clip by
// default). Re-apply scrollWithin / clip styling here if a v7
// consumer ever needs the sticky-header-with-scrollable-body shape
// v6 uses.
//
// Returned shape `{ target, close }` matches openPortalModal's
// contract. Backdrop-click + ESC dismissal are handled by the
// shared widgetModals.js document listener that's already loaded
// by the v7 donations layout (no extra wiring needed here).

const cache = new Map()

const applyOptions = (element, { maxWidth }) => {
  if (maxWidth) element.style.setProperty('--widget-modal-max-width', maxWidth)
  else element.style.removeProperty('--widget-modal-max-width')
}

export const injectLocalPortalModal = ({ id, ariaLabel, maxWidth, scrollWithin }) => {
  const cached = cache.get(id)
  if (cached) {
    applyOptions(cached.element, { maxWidth })
    cached.element.showModal()
    return { target: cached.target, close: cached.close }
  }

  const element = document.createElement('dialog')
  element.className = 'widget-modal'
  if (ariaLabel) element.setAttribute('aria-label', ariaLabel)

  // The portal target is the .widget-modal-body (the centered card
  // surface). The React content provides everything inside it
  // (close button, headings, form fields) — matches what the iframe
  // path renders inside the renderModal dialog.
  const target = document.createElement('div')
  target.className = 'widget-modal-body'
  element.appendChild(target)

  document.body.appendChild(element)
  applyOptions(element, { maxWidth })

  element.showModal()

  const close = () => element.close()
  cache.set(id, { element, target, close })
  return { target, close }
}
